import { arrayToObject } from "@lib/TypeUtil"
import { Contact, ContactType } from "@model/contacts/Contact"
import { newServiceSubType, ServiceRequestType, ServiceSubType } from "@model/serviceRequests/ServiceRequest"
import { newThresholdGroup, ThresholdGroup } from "@model/Threshold"
import { WorkRequestType } from "@model/workRequests/WorkRequest"

export type CompanySettings = {
    readonly mechanics: Contact[]
    readonly mechanicTypes: ContactType[]
    readonly serviceSubTypes: Record<ServiceRequestType, ServiceSubType>
    readonly workOrderAutomation: WorkOrderAutomationSettings
    readonly workOrderRequiredFields: WorkOrderRequiredFields
    readonly thresholds: ThresholdSettings
}

/**
 * Fields that become required when moving a Work Order to Work Completed or Closed.
 * Resolved server side against the company hierarchy: isReadOnly is true when the selection was
 * inherited from an ancestor company, in which case this company cannot change it.
 */
export type WorkOrderRequiredFields = {
    companyId: number
    workOrderFields: WorkOrderRequiredFieldsGroup
    serviceItemFields: ServiceItemRequiredFieldsGroup
    isReadOnly: boolean
}

export type WorkOrderRequiredFieldsGroup = {
    assignedTo: boolean
    site: boolean
}

export type ServiceItemRequiredFieldsGroup = {
    workPerformedBy: boolean
    serviceCode: boolean
    laborHours: boolean
    laborCost: boolean
    partsCost: boolean
    miscCost: boolean
    mechanicsNotes: boolean
    measurements: boolean
    attachments: boolean
}

export type WorkOrderRequiredFieldKey = keyof WorkOrderRequiredFieldsGroup
export type ServiceItemRequiredFieldKey = keyof ServiceItemRequiredFieldsGroup

/**
 * Display labels, in the order the ticket lists the fields. Insertion order drives both the admin
 * checkbox order and the order missing fields are reported to the user, so keep the two aligned.
 */
export const workOrderRequiredFieldLabels: Record<WorkOrderRequiredFieldKey, string> = {
    assignedTo: "Assigned To",
    site: "Site",
}

export const serviceItemRequiredFieldLabels: Record<ServiceItemRequiredFieldKey, string> = {
    workPerformedBy: "Work Performed By",
    serviceCode: "Service Code",
    laborHours: "Labor Hours",
    laborCost: "Labor Cost",
    partsCost: "Parts Cost",
    miscCost: "Misc Cost",
    mechanicsNotes: "Mechanic's Notes",
    measurements: "Measurements",
    attachments: "Attachments",
}

export const workOrderRequiredFieldKeys = Object.keys(workOrderRequiredFieldLabels) as WorkOrderRequiredFieldKey[]

export const serviceItemRequiredFieldKeys = Object.keys(
    serviceItemRequiredFieldLabels
) as ServiceItemRequiredFieldKey[]

export type WorkOrderAutomationSettings = {
    id: number | null
    companyId: number
    [WorkRequestType.Inspection]: boolean
    [WorkRequestType.Preventative]: boolean
    [WorkRequestType.Service]: boolean
    applyToSubCompanies: boolean | null
    isReadOnly: boolean
}

export type ThresholdSettings = {
    [WorkRequestType.Inspection]: ThresholdGroup
    [WorkRequestType.Preventative]: ThresholdGroup
    [WorkRequestType.Service]: ThresholdGroup
}

export function thresholdSettingsHasValue(settings: ThresholdSettings): boolean {
    return Object.values(settings).some(
        (value: ThresholdGroup) => value.hours.value !== null || value.miles.value !== null || value.days.value !== null
    )
}

export function newCompanySettings(): CompanySettings {
    return {
        mechanics: [],
        mechanicTypes: [
            ContactType.ThirdParty,
            ContactType.Contractor,
            ContactType.Customer,
            ContactType.Employee,
            ContactType.Vendor,
        ],
        serviceSubTypes: arrayToObject(Object.values(ServiceRequestType), (type) => [type, newServiceSubType(type)]),
        workOrderAutomation: newWorkOrderAutomationSettings(),
        workOrderRequiredFields: newWorkOrderRequiredFields(),
        thresholds: newThresholdSettings(),
    }
}

export function newWorkOrderRequiredFields(): WorkOrderRequiredFields {
    return {
        companyId: 0,
        workOrderFields: {
            assignedTo: false,
            site: false,
        },
        serviceItemFields: {
            workPerformedBy: false,
            serviceCode: false,
            laborHours: false,
            laborCost: false,
            partsCost: false,
            miscCost: false,
            mechanicsNotes: false,
            measurements: false,
            attachments: false,
        },
        isReadOnly: false,
    }
}

export function newThresholdSettings(): ThresholdSettings {
    return {
        [WorkRequestType.Inspection]: newThresholdGroup(),
        [WorkRequestType.Preventative]: newThresholdGroup(),
        [WorkRequestType.Service]: newThresholdGroup(),
    }
}

export function newWorkOrderAutomationSettings(): WorkOrderAutomationSettings {
    return {
        id: null,
        companyId: 0,
        [WorkRequestType.Inspection]: false,
        [WorkRequestType.Preventative]: false,
        [WorkRequestType.Service]: false,
        applyToSubCompanies: null,
        isReadOnly: false
    }
}
